/**
 * Main-thread client for the Sapling-params decompress Web Worker.
 *
 * Spawns the worker lazily (only when a decompression is actually
 * requested), reuses it across multiple shards in one onboarding pass,
 * and tears it down once the canonical bytes are in IndexedDB so we
 * don't pin its WASM heap.
 *
 * Single-flight by design: at most one decompression is in flight at
 * any moment. Both Sapling shards are ~3 s + ~25 s sequentially which
 * is faster (and lower peak memory) than parallel decompression of
 * both — we don't want two ~200 MB WASM heaps live simultaneously on
 * a phone.
 */

import type {
  DecompressRequest,
  DecompressResponse,
} from "./decompress-worker";

let worker: Worker | null = null;
let inflight: Promise<unknown> | null = null;
let nextId = 1;

function getWorker(): Worker {
  if (!worker) {
    // Vite resolves this to a worker bundle, transferring the
    // sapling-codec-wasm import alongside.
    worker = new Worker(
      new URL("./decompress-worker.ts", import.meta.url),
      { type: "module" },
    );
  }
  return worker;
}

export async function decompressShardInWorker(args: {
  compressed: Uint8Array;
  expectedSha256: string;
}): Promise<Uint8Array> {
  // Serialise — see file docstring.
  while (inflight) {
    try {
      await inflight;
    } catch {
      // ignore; we'll start fresh
    }
  }

  const id = nextId++;
  const req: DecompressRequest = {
    id,
    compressed: args.compressed,
    expectedSha256: args.expectedSha256,
  };

  const w = getWorker();
  const p = new Promise<Uint8Array>((resolve, reject) => {
    const onMessage = (ev: MessageEvent<DecompressResponse>) => {
      if (ev.data.id !== id) return;
      w.removeEventListener("message", onMessage);
      w.removeEventListener("error", onError);
      if (ev.data.kind === "ok") {
        resolve(ev.data.canonical);
      } else {
        reject(new Error(ev.data.message));
      }
    };
    const onError = (e: ErrorEvent) => {
      w.removeEventListener("message", onMessage);
      w.removeEventListener("error", onError);
      reject(new Error(e.message || "decompress worker crashed"));
    };
    w.addEventListener("message", onMessage);
    w.addEventListener("error", onError);
    w.postMessage(req, [req.compressed.buffer]);
  });
  inflight = p.finally(() => {
    inflight = null;
  });
  return p;
}

/** Tear down the worker. Called after all shards are processed. */
export function disposeDecompressWorker(): void {
  if (worker) {
    worker.terminate();
    worker = null;
  }
}
